import { env } from '@services/config/env'
import { getFirebaseIdToken } from '@services/config/firebase'
import type {
  SeatMapData,
  ReserveSeatsResponse,
} from '@components/SeatMap/SeatMap.types'

const BASE_URL = env.legacyApiBaseUrl

const MOCK_SEAT_MAP: SeatMapData = {
  eventId: 1,
  eventName: 'Concierto Sinfónico — Pumapungo (MOCK)',
  seatingType: 'NUMBERED',
  sections: [
    {
      id: 1,
      name: 'ESCENARIO',
      subSection: 'Centro',
      rows: ['A', 'B', 'C'].map((rowLabel, ri) => ({
        rowLabel,
        seats: Array.from({ length: 10 }, (_, i) => ({
          id: ri * 10 + i + 1,
          seatNumber: i + 1,
          label: `${rowLabel}-${String(i + 1).padStart(2, '0')}`,
          status: i === 3 ? 'SOLD' : i === 5 ? 'RESERVED_TEMP' : 'AVAILABLE',
        })),
      })),
    },
    {
      id: 2,
      name: 'PLATEA ALTA',
      subSection: 'Centro',
      rows: ['AA', 'BB'].map((rowLabel, ri) => ({
        rowLabel,
        seats: Array.from({ length: 8 }, (_, i) => ({
          id: 100 + ri * 8 + i + 1,
          seatNumber: i + 1,
          label: `${rowLabel}-${String(i + 1).padStart(2, '0')}`,
          status:
            i === 1 ? 'BLOCKED_ADMIN' : i === 4 ? 'SOLD' : 'AVAILABLE',
        })),
      })),
    },
  ],
}

/**
 * Returns Authorization: Bearer <firebase-token> header.
 * Falls back to X-Api-Token if no Firebase session exists.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getFirebaseIdToken()
  if (token) {
    return { Authorization: `Bearer ${token}` }
  }
  return { 'X-Api-Token': env.apiToken }
}

/**
 * Fetch the full seat map for an event, including availability per seat.
 * In mock mode returns static fixture data.
 */
export async function fetchSeatMap(eventId: number): Promise<SeatMapData> {
  if (env.apiMode === 'mock') {
    return { ...MOCK_SEAT_MAP, eventId }
  }
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE_URL}/event/${eventId}/seat-map`, { headers })
  if (!res.ok) throw new Error(`seat-map fetch failed: ${res.status}`)
  return res.json() as Promise<SeatMapData>
}

interface ReservePayload {
  eventId: number
  seatIds: number[]
}

/**
 * Reserve one or more seats for 10 minutes.
 * Returns the server-generated sessionToken and expiry time.
 * On 409: throws an error with `conflictingSeatIds` attached.
 */
export async function reserveSeats(
  payload: ReservePayload,
): Promise<ReserveSeatsResponse> {
  if (env.apiMode === 'mock') {
    return {
      sessionToken: `mock-token-${Date.now()}`,
      reservedUntil: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      seats: payload.seatIds.map((id) => ({ seatId: id, label: `MOCK-${id}` })),
    }
  }
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE_URL}/seat-availability/reserve`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (res.status === 409) {
    const body = await res.json()
    const err = new Error(body.message ?? 'Seat conflict') as Error & {
      conflictingSeatIds?: number[]
    }
    err.conflictingSeatIds = body.conflictingSeatIds
    throw err
  }
  if (!res.ok) throw new Error(`reserve failed: ${res.status}`)
  return res.json() as Promise<ReserveSeatsResponse>
}

/**
 * Release a previously reserved seat.
 * sessionToken is sent as X-Session-Token header (not query param — OWASP-A02).
 * eventId is required by the backend to locate the seat_availability record.
 */
export async function releaseSeat(
  seatId: number,
  eventId: number,
  sessionToken: string,
): Promise<void> {
  if (env.apiMode === 'mock') return

  const url = new URL(`${BASE_URL}/seat-availability/release`)
  url.searchParams.set('seatId', String(seatId))
  url.searchParams.set('eventId', String(eventId))

  const headers = await getAuthHeaders()
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: { ...headers, 'X-Session-Token': sessionToken },
  })
  if (!res.ok && res.status !== 404) {
    throw new Error(`release failed: ${res.status}`)
  }
}

export interface ConfirmPurchasePayload {
  eventId: number
  localityId: number
  seatIds: number[]
  sessionToken: string
  clientEmail: string
  clientName: string
  clientPhone?: string
  /** Orden existente creada por saveSale. Si se provee, confirmPurchase actualiza
   *  esa venta en lugar de crear una nueva — evita el duplicado de ventas. */
  existingOrderId?: string
}

export interface ConfirmPurchaseResponse {
  orden: string
  total: number
  seatLabels: string[]
  message: string
}

/**
 * Confirm a numbered seat purchase after PayPhone payment is approved.
 * Marks seats as SOLD, generates PDF and sends email via MiServicioBoletos.
 * Called from PaymentReturnPage after consultarPayphone succeeds.
 */
export async function confirmPurchase(
  payload: ConfirmPurchasePayload,
): Promise<ConfirmPurchaseResponse> {
  if (env.apiMode === 'mock') {
    return {
      orden: `MOCK-${Date.now()}`,
      total: 0,
      seatLabels: payload.seatIds.map((id) => `MOCK-${id}`),
      message: 'mock confirm ok',
    }
  }
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE_URL}/seat-availability/confirm`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string }
    throw new Error(body.message ?? `confirm failed: ${res.status}`)
  }
  return res.json() as Promise<ConfirmPurchaseResponse>
}
